import * as R from 'ramda';
import { abbreviateNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  MODEL_TYPES,
  ModelSegment,
  SCENARIO_METRIC_ENUM,
  ScenarioDateRow
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { getChannelSeriesColor, shouldUsePrimaryPalette } from '../../utils/colors';

export interface ScenarioPlanningChartData {
  baselineValue: number;
  confidenceInterval?: number | [number, number];
  directValue: number;
  hasScenario: boolean;
  id?: string;
  // ==== HROAS: every distinct model that fed this bar's bucket (via flightingData) — length 1
  // for an unblended bucket (e.g. any Model grouping), length 2+ when Channel/Platform/Tactic
  // grouping merged rows from more than one model. Lets the tooltip show real source attribution
  // instead of any single per-bucket model id. ====
  contributingModels?: { id: string; label: string }[];
  // ==== HROAS: per-model revenue segments of this bar (weighted). Present only
  // when segments are on and the metric is a revenue-type metric. ====
  segments?: ModelSegment[];
  indirectValue: number;
  isDemandCapture?: boolean;
  label?: string;
  flightingData: ScenarioDateRow[];
  metric: SCENARIO_METRIC_ENUM;
  modelType: MODEL_TYPES;
  showActualized: boolean;
  showBreakout: boolean;
  showPercentageView: boolean;
  // Grand total across every channel for this metric (unaffected by any channel search filter) —
  // the denominator for "% of total" labels/tooltips when showPercentageView is on.
  totalValue: number;
  useHistoricalFlighting: boolean;
}

export interface ScenarioPlanningChartEntry extends ScenarioPlanningChartData {
  color: string;
  // One shade of `color` per entry in `segments` (same order); set by the chart.
  segmentFills?: string[];
  actualizedValue: number;
  labelValue: number;
  lineValue: number;
  renderDirectValue: number;
  renderIndirectValue: number;
  // `waterFallValue > 0` is also the bar shape's signal for "this bar floats
  // off the x-axis, round its bottom corners too." Anchored bars (waterfall
  // off, or the first bar in a waterfall) have waterFallValue === 0.
  waterFallValue: number;
}

type SparkChartRow = ScenarioDateRow & { sparkValue: number };

export const SCENARIO_NEGATIVE_PILL_COLOR = '#FF8D8D';

export const abbreviateScenarioValue = (
  value: number,
  options: { monetary?: boolean; showSign?: boolean } = {}
): string => {
  const { monetary = false, showSign = false } = options;
  const abs = Math.abs(value);
  const decimals = abs >= 100_000 && abs < 1_000_000 ? 0 : 2;
  const num = abbreviateNumber(abs, decimals);
  const getSign = (): string => {
    if (value < 0) {
      return '-';
    }
    return showSign && value > 0 ? '+' : '';
  };
  const sign = getSign();
  const dollar = monetary ? '$' : '';
  return `${sign}${dollar}${num}`;
};

export const FLAT_LINE_VAL = 100;
export const NUM_POINTS = 20;
export const SPARK_CHART_DATA_KEY = 'sparkValue';
export const SPARK_CHART_HEIGHT = 27;
export const SPARK_CHART_WIDTH = 197;
export const SPARKCHART_LINE_WIDTH = 2;

// Spend/revenue deltas under this dollar amount are treated as no change so the tooltip
// and x-axis pills don't render misleading "<1%" labels for sub-$10 noise. Dollar values only:
// non-dollar metrics use their own floors (getAbsoluteChangeFloor).
export const MIN_CHANGE_THRESHOLD = 10;
// ROAS et al render at 2 decimals, so changes below 0.01 are invisible in the value cell —
// the pill should match and report 0% rather than "<1%".
const FRACTIONAL_CHANGE_THRESHOLD = 0.01;

const DOLLAR_METRICS: ReadonlySet<SCENARIO_METRIC_ENUM> = new Set([
  SCENARIO_METRIC_ENUM.REVENUE,
  SCENARIO_METRIC_ENUM.SPEND
]);

// KPI volume is a count shown as a whole number, so under half a unit is invisible in the cell.
// Assumes 0 display decimals; revisit this floor if the KPI volume cell ever shows fractions.
const KPI_VOLUME_CHANGE_THRESHOLD = 0.5;

const getAbsoluteChangeFloor = (metric: SCENARIO_METRIC_ENUM): number => {
  if (DOLLAR_METRICS.has(metric)) {
    return MIN_CHANGE_THRESHOLD;
  }
  return metric === SCENARIO_METRIC_ENUM.KPI_VOLUME
    ? KPI_VOLUME_CHANGE_THRESHOLD
    : FRACTIONAL_CHANGE_THRESHOLD;
};

// Below this floor, render no bar (vs. floor at MIN_BAR_HEIGHT). Matches each
// metric's display precision so a "$0" or "0.00" label never carries a bar.
const DOLLAR_RENDER_FLOOR = 1;
const FRACTIONAL_RENDER_FLOOR = FRACTIONAL_CHANGE_THRESHOLD;

export const getBarRenderFloor = (metric: SCENARIO_METRIC_ENUM): number =>
  DOLLAR_METRICS.has(metric) ? DOLLAR_RENDER_FLOOR : FRACTIONAL_RENDER_FLOOR;

// base === 0 with non-zero change returns ±Infinity (formatter renders "∞%")
// rather than a misleading 0%. The metric-aware change floor ensures the pill
// only fires when the value cell would visibly change at its display precision.
export const computeDeltaPercent = (
  change: number,
  base: number,
  metric?: SCENARIO_METRIC_ENUM
): number => {
  if (metric && Math.abs(change) < getAbsoluteChangeFloor(metric)) {
    return 0;
  }
  if (base === 0) {
    if (change === 0) {
      return 0;
    }
    return change > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }
  return (100.0 * change) / base;
};

// Percent change for the Edit Scenario "applied" columns, rounded to 2 decimals. A dollar-prefixed
// column treats a gap under the dollar noise floor as no change, like the cards and pills do.
export const getDisplayedDeltaPercent = (
  current: number,
  baseline: number,
  prefix: string
): number => {
  if (baseline === 0 || (prefix === '$' && Math.abs(current - baseline) < MIN_CHANGE_THRESHOLD)) {
    return 0;
  }
  return Math.round(((100.0 * (current - baseline)) / baseline) * 100) / 100;
};

// For callers whose displayed precision differs from the metric's default
// floor (e.g. K/M-formatted dollars). Returns 0% when both values round equal, or when the raw
// gap is under `absoluteFloor` (noise that straddles a rounding bucket would otherwise print a step).
export const computeDisplayDeltaPercent = (
  scenario: number,
  baseline: number,
  roundForDisplay: (value: number) => number,
  absoluteFloor = 0
): number => {
  if (Math.abs(scenario - baseline) < absoluteFloor) {
    return 0;
  }
  const roundedScenario = roundForDisplay(scenario);
  const roundedBaseline = roundForDisplay(baseline);
  if (roundedScenario === roundedBaseline) {
    return 0;
  }
  return computeDeltaPercent(roundedScenario - roundedBaseline, roundedBaseline);
};

// Matches the spark card K/M formatter's precision: $X.XM → $100K, $X.XK → $100, else $1.
export const roundToCompactDollars = (value: number): number => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return Math.round(value / 100_000) * 100_000;
  }
  if (abs >= 1_000) {
    return Math.round(value / 100) * 100;
  }
  return Math.round(value);
};

const METRIC_TO_FIELD: Record<SCENARIO_METRIC_ENUM, keyof ScenarioDateRow> = {
  [SCENARIO_METRIC_ENUM.CPX]: 'directRevenue',
  [SCENARIO_METRIC_ENUM.KPI_VOLUME]: 'directRevenue',
  [SCENARIO_METRIC_ENUM.REVENUE]: 'directRevenue',
  [SCENARIO_METRIC_ENUM.ROAS]: 'directRevenue',
  [SCENARIO_METRIC_ENUM.SPEND]: 'directSpend'
};

export const isMonetaryMetric = (metric: SCENARIO_METRIC_ENUM): boolean =>
  metric === SCENARIO_METRIC_ENUM.CPX ||
  metric === SCENARIO_METRIC_ENUM.REVENUE ||
  metric === SCENARIO_METRIC_ENUM.SPEND;

export const getSolidHeight = (entry: ScenarioPlanningChartEntry): number => {
  if (!entry.showBreakout) {
    return entry.showActualized
      ? entry.renderDirectValue + Math.abs(entry.renderIndirectValue)
      : entry.renderDirectValue;
  }
  return entry.renderDirectValue;
};

export const getIndirectHeight = (entry: ScenarioPlanningChartEntry): number => {
  if (!entry.showBreakout) {
    return 0;
  }
  return Math.abs(entry.renderIndirectValue);
};

/**
 * Border wraps the full bar (direct + indirect) when:
 * - Demand generation channel with actualized breakout visible
 * Demand capture channels get a direct-only border instead (handled in the bar shape).
 */
export const shouldShowFullBarBorder = (entry: ScenarioPlanningChartEntry): boolean => {
  if (!entry.showBreakout || !entry.showActualized) {
    return false;
  }
  return !entry.isDemandCapture;
};

export const parseChannelId = (
  id: string
): { channel: string; platform: string; tactic: string } => {
  const stripped = id.replace(/_(dc|dg)$/, '');
  const [channel, platform = '', tactic = ''] = stripped.split('_');
  return { channel, platform, tactic };
};

export const buildHeaderLabel = (id: string): string => {
  const { channel, platform, tactic } = parseChannelId(id);
  let label = channel;
  if (platform && R.toLower(platform) !== 'n/a') {
    label = `${label} > ${platform}`;
  }
  if (tactic && R.toLower(tactic) !== 'n/a') {
    label = `${label} > ${tactic}`;
  }
  return label;
};

export const buildChannelColorMap = (
  entries: { id?: string }[],
  useTestAccountPalette?: boolean
): Record<string, string> => {
  const uniqueKeys = new Set<string>();
  for (const entry of entries) {
    const colorKey = entry.id ? entry.id.split('_')[0] : undefined;
    if (colorKey) {
      uniqueKeys.add(colorKey);
    }
  }
  const sortedKeys = Array.from(uniqueKeys).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
  const usePrimary = shouldUsePrimaryPalette(sortedKeys.length);
  const map: Record<string, string> = {};
  sortedKeys.forEach((key, index) => {
    map[key] = getChannelSeriesColor(index, usePrimary, useTestAccountPalette);
  });
  return map;
};

export const sampleFlightingData = (
  flightingData: ScenarioDateRow[],
  numPoints = NUM_POINTS
): ScenarioDateRow[] =>
  flightingData.length <= numPoints
    ? flightingData
    : Array.from(
        { length: numPoints },
        (_, i) => flightingData[Math.round((i * (flightingData.length - 1)) / (numPoints - 1))]
      );

export const buildSparkChartData = (
  flightingData: ScenarioDateRow[],
  metric: SCENARIO_METRIC_ENUM,
  useHistoricalFlighting: boolean
): SparkChartRow[] => {
  const sampled = sampleFlightingData(flightingData);
  const sourceField = METRIC_TO_FIELD[metric];
  return sampled.map(entry => ({
    ...entry,
    [SPARK_CHART_DATA_KEY]: useHistoricalFlighting
      ? ((entry[sourceField] as number) ?? 0)
      : FLAT_LINE_VAL
  }));
};

export interface SegmentLayout {
  segments: ModelSegment[];
  fills: string[];
  fractions: number[]; // 0..1, bottom-up, sums to 1
}

/**
 * How the solid bar splits into model segments, or undefined when it has no usable split: no
 * `segments` or `segmentFills`, fewer than two segments, any negative segment (a share of a
 * total that includes a negative has no honest reading), or no positive total. The bar shows
 * actualized revenue when `showActualized`, else direct, so segments use the matching value.
 */
export const getSegmentLayout = (entry: ScenarioPlanningChartEntry): SegmentLayout | undefined => {
  const { segments, segmentFills } = entry;
  if (!segments || !segmentFills || segments.length < 2) {
    return undefined;
  }
  const values = segments.map(seg => (entry.showActualized ? seg.actualized : seg.direct));
  const total = values.reduce((sum, v) => sum + v, 0);
  if (values.some(v => v < 0) || total <= 0) {
    return undefined;
  }
  return { segments, fills: segmentFills, fractions: values.map(v => v / total) };
};
